import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@core/services/core/auth/auth.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { ContactService } from '@core/services/http-services/contact.service';
import { ProfileService } from '@core/services/http-services/profile.service';
import { OthersOperation } from '@models/operation-register';
import { ProfileUserInfo } from '@models/profile';
import { UserDocumentType } from '@models/user';
import { ContactChangeContextState, ReasonContextState } from '@modules/contact/models/contact-change';
import { TranslateService } from '@ngx-translate/core';
import { ValidatorEmail, ValidatorMatchValues } from '@utils/custom-validators';
import { spinner } from '@utils/rxjs';
import { StoreUtils } from '@utils/store';
import { isValidPartialNie, isValidPartialNif } from '@utils/utils';

@Injectable({
  providedIn: 'root'
})
export class ContactContextService extends StoreUtils<ContactChangeContextState>{
  private policySelected;
  private roProductId = '';
  private documentNumber: string;
  public isLoadData: boolean;

  constructor(
    private profileService: ProfileService,
    private ui: UiHelperService,
    private contactService: ContactService,
    private nav: NavigationHelper,
    private or: OperationRegisterService,
    private translate: TranslateService,
    private authService: AuthService
  ) {
    super();
  }

  setPolicySelected(policy) {
    this.policySelected = policy;
  }

  setDocumentNumber(documentNumber) {
    this.documentNumber = documentNumber;
  }

  getDocumentNumber() {
    return this.documentNumber;
  }

  getReasonsList(): Array<ReasonContextState> {
    return [
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_CAR'), type: ['car'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_HOME'), type: ['home'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_HEALTH'), type: ['health'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_SAVING'), type: ['saving'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_LIFE'), type: ['life', 'collective-life'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_DEATH'), type: ['death'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_ACCIDENT'), type: ['accident'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_OTHER'), type: ['other'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_PENSION'), type: ['pension'] },
      { label: this.translate.instant('CONTACT_FORM.PRODUCTS_SAVING_INVESTMENT'), type: ['investment', 'investment-nova'] }
    ];
  }

  getContactFormGroup() {
    const formContact = new FormGroup({
      reason: new FormControl('', Validators.required),
      policy: new FormControl(''),
      contractNumber: new FormControl('', Validators.required),
      message: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, ValidatorEmail])
    });

    return formContact;
  }

  async initializeContactFormGroup(formContact) {
    const user = await this.getUserInfo();

    if (user) {
      formContact.patchValue({ email: user.email });
    }

    return formContact;
  }

  sendContactFormGroup(formContact) {
    const body = {
      productType: formContact.value['reason'],
      product: formContact.value['policy'],
      contractNumber: this.checkPolicyNumber(formContact.value['contractNumber']),
      consultDetail: formContact.value['message'],
      email: formContact.value['email'],
      productFound: formContact.value['contractNumber'] ===
      this.translate.instant('CONTACT_FORM.POLICY_NOT_FOUND') ? false : true
    };

    this.completeState();

    this.contactService.contactForm(body, this.policySelected)
      .pipe(spinner('send-contact-form'))
      .subscribe(
        success => { this.nav.to('contact/success', { success }); },
        error => { this.nav.to('contact/error'); }
      );
  }

  getSupportFormGroup() {
    const formSupport = new FormGroup({
      documentType: new FormControl('', Validators.required),
      document: new FormControl('', Validators.required),
      name: new FormControl('', Validators.required),
      lastname: new FormControl('', Validators.required),
      lastname2: new FormControl(''),
      email: new FormControl('', [Validators.required, ValidatorEmail]),
      confirmEmail: new FormControl('', [Validators.required, ValidatorEmail, ValidatorMatchValues('email')]),
      message: new FormControl('', Validators.required),
      phoneNumber: new FormControl('')
    });

    return formSupport;
  }

  async initializeSupportFormGroup(formSupport) {
    const user = await this.getUserInfo();

    if (this.documentNumber) {
      formSupport.patchValue({ documentType: this.getDocumentTypeByDocumentNumber(this.documentNumber) });
      formSupport.patchValue({ document: this.sanitizeDocument(this.documentNumber) });
    }
    if (user) {
      formSupport.patchValue({ documentType: user.documentType });
      formSupport.patchValue({ document: this.sanitizeDocument(user.documentNumber) });
      formSupport.patchValue({ name: user.name });
      formSupport.patchValue({ lastname: user.lastname });
      formSupport.patchValue({ lastname2: user.lastname2 });
      formSupport.patchValue({ email: user.email });
      formSupport.patchValue({ confirmEmail: user.email });
      formSupport.patchValue({ phoneNumber: user.phones[0] });
    }

    this.setDocumentNumber(null);
    return formSupport;
  }

  sendSupportFormGroup(formSupport) {
    const body = {
      documentType: formSupport.get('documentType').value,
      documentId: formSupport.get('document').value,
      name: formSupport.get('name').value,
      firstName: formSupport.get('lastname').value,
      lastName: formSupport.get('lastname2').value,
      email: formSupport.get('email').value,
      confirmedEmail: formSupport.get('confirmEmail').value,
      consultDetail: formSupport.get('message').value,
      phoneNumber: formSupport.get('phoneNumber').value
    };

    this.contactService.supportForm(body, this.policySelected, this.roProductId)
      .pipe(spinner('send-support-form'))
      .subscribe(
        success => { this.nav.to('support/success', { success }); },
        error => { this.nav.toNew('support/error'); },
      );
  }

  navToSupportForm(productId: string) {
    this.roProductId = productId;
    this.or.regiesterContact(OthersOperation.SUPPORT, productId, this.documentNumber);
    this.nav.toNew('support', { state : { productId }});
  }

  private getDocumentTypeByDocumentNumber(documentNumber: string) {
    return isValidPartialNif(documentNumber) ? UserDocumentType.NIF
      : isValidPartialNie(documentNumber) ? UserDocumentType.NIE : null;
  }

  private sanitizeDocument(documentNumber: string) {
    let documentSanitize = documentNumber;
    if (documentNumber.length > 9) {
      documentSanitize = documentNumber[0] === '0' ? documentNumber.slice(1) : documentNumber;
    }
    const isValidPartialNifOrNie = isValidPartialNif(documentSanitize) || isValidPartialNie(documentSanitize);
    return isValidPartialNifOrNie ? documentSanitize : documentNumber;
  }

  private checkPolicyNumber(policyNumber) {
    const POLICIES_NOT_FOUND = [
      this.translate.instant('CONTACT_FORM.POLICY_NOT_FOUND'),
      this.translate.instant('CONTACT_FORM.OTHER_REASON')
    ];

    const notFoundPolicy = POLICIES_NOT_FOUND.indexOf(policyNumber) > -1;

    return notFoundPolicy ? null : policyNumber;
  }

  private async getUserInfo() {
    const isUserAuthenticated = this.authService.getState().isLogged;
    if (!isUserAuthenticated) {
      return;
    }
    await this.ui.presentLoading('user-info');

    return await this.profileService.getUserInfo().toPromise()
      .catch( e => null as ProfileUserInfo )
      .finally(() => {
        this.ui.dismissLoading('user-info');
      });
  }

}
